import { useLang } from "@/contexts/LangContext";
import SectionReveal from "@/components/SectionReveal";
import { Stethoscope, Languages, Briefcase, ArrowRight } from "lucide-react";

const IMAGES = {
  medical: "/img/services/acupuncture.png",
  mandarin: "/img/services/calligraphy.png",
  business:
    "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=80",
};

const ICONS = { medical: Stethoscope, mandarin: Languages, business: Briefcase };

const Services = () => {
  const { t } = useLang();
  const tt = t.services;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-[#F7F5F0] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionReveal className="max-w-3xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#0E2A5C] font-semibold mb-4">
            ✦ {tt.eyebrow}
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#121212] leading-[1.05]">
            {tt.title}
          </h2>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
            {tt.subtitle}
          </p>
        </SectionReveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {tt.items.map((s, i) => {
            const key = ["medical", "mandarin", "business"][i];
            const Icon = ICONS[key];
            return (
              <SectionReveal
                key={key}
                delay={i * 0.1}
                data-testid={`service-card-${key}`}
                className="group relative overflow-hidden bg-white border border-[#EAE6DF] hover:border-[#0E2A5C]/30 transition-all duration-500 flex flex-col"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={IMAGES[key]}
                    alt={s.name}
                    className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                  <div className="absolute top-5 left-5 w-12 h-12 bg-white/95 backdrop-blur-sm flex items-center justify-center text-[#0E2A5C] shadow-lg">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                </div>

                <div className="p-7 md:p-8 flex flex-col flex-1">
                  <h3 className="font-heading text-2xl md:text-3xl text-[#121212]">
                    {s.name}
                  </h3>
                  <p className="mt-4 text-[#4A4A4A] leading-relaxed flex-1">
                    {s.body}
                  </p>
                  <ul className="mt-5 space-y-2">
                    {s.features.map((f, j) => (
                      <li
                        key={j}
                        className="text-sm text-[#4A4A4A] flex items-start gap-2"
                      >
                        <span className="w-1 h-1 bg-[#C62828] rounded-full mt-2 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`service-inquiry-${key}`}
                    onClick={() => scrollTo("contact")}
                    className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-wider text-[#0E2A5C] font-semibold border-b-2 border-[#0E2A5C] pb-1 self-start hover:gap-3 hover:text-[#C62828] hover:border-[#C62828] transition-all duration-300"
                  >
                    {tt.cta} <ArrowRight size={14} />
                  </button>
                </div>
              </SectionReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
